// Import packages
import React, {useEffect, useRef, useState} from 'react';
import {connect, useDispatch, useSelector} from "react-redux";
import DatePicker, {registerLocale} from "react-datepicker";

// Import assets
import "react-datepicker/dist/react-datepicker.css";
import './calendar.scss';
import {calendar_img, GoogleCalendarIcon, OutlookCalendarIcon, SettingsIcon} from "../../../image";

//Import components
import EventItem from "./eventItem/EventItem";
import Synchronize from "./synchronize/Synchronize";
import SettingsModal from "./settingsModal/SettingsModal";

//Import utils
import {getPropsFromState} from "../../../redux/mapStateToProps";
import {
    GetAuthorizationUrlOutlook,
    GetCalendarsEvents,
    GetCalendarSynchronize, GetGoogleAuth, GoogleAuth
} from "../../../redux/actions";
import {changeTimeFormat, hasPermission} from "../../../utils/helperFunctions";
import {useTranslation} from "react-i18next";
import config from "../../../utils/config";
import {enGB} from 'date-fns/locale';
import dayjs from "dayjs";
import {ROLE_ITEMS} from "../../../utils/constants";
import {Button} from "../../../modules/Button";
import ConnectedCalendarModal from "../../../modules/Calendar/ConnectedCalendarModal/ConnectedCalendarModal";
import label from "../../../image/images/calendar-connect.png";
import {Modal, Tour} from "antd";
import {GoogleOAuthProvider, useGoogleLogin} from "@react-oauth/google";
import labelReauth from "../../../image/images/calendar-reauth.png";
import {useLocation} from "react-router-dom";
import WelcomeModal from "../../../modules/WelcomeModal/WelcomeModal";
import {
    KEY_LOCAL_STORAGE_DONT_VISIBLE_WELCOME_MODAL,
    KEY_LOCAL_STORAGE_FLAG_STEP_ONE,
    STEPS
} from "../../../modules/Tour/helpers";
import {UpdateActiveStep} from "../../../redux/actions/tourActions";
import TourFirstStep from "../../../modules/Tour/components/TourFirstStep/TourFirstStep";
import {useTourActions} from "../../../modules/Tour/hooks";

function Calendar(props) {
    const {calendarEvents, clientId, googleToken} = props;
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const location = useLocation();
    const activeStep = useSelector((state) => state.tour?.activeStep);
    const dispatch = useDispatch();
    const {handleClickNextStep2List} = useTourActions();

    const [date, setDate] = useState()

    const [googleUser, setGoogleUser] = useState(undefined);
    const [eventList, setEventList] = useState({});
    const [loading, setLoading] = useState(false);
    const [googleSignature, setGoogleSignature] = useState('');

    const targetRef = useRef(null);
    const outlookInfo = useSelector(state => state.userCalendars.outlookInfo)
    const userCalendars = useSelector(state => state.userCalendars);
    const searchParams = new URLSearchParams(location.search);
    const isCalendarAdded = searchParams.get('isGoogleCalendarAdd');
    const {t, i18n} = useTranslation();

    registerLocale('en-GB', enGB);

    const OutlookAuth = async () => {
        try {
            const res = await GetAuthorizationUrlOutlook();
            window.location.href = res.url;
        } catch (e) {
            console.error(e)
        }
    }

    const openSuccessModalToNewUser = async () => {
        const data = localStorage.getItem('google_scope')
        handleOnSuccess(JSON.parse(data));
        searchParams.delete('isGoogleCalendarAdd');
        localStorage.removeItem('google_scope')
    }

    useEffect(() => {
        if (isCalendarAdded === 'true') {
            openSuccessModalToNewUser()
        }
    }, [])

    useEffect(() => {
        props.GetGoogleAuth()

        calendarEventsList()
    }, [googleToken])


    useEffect(() => {
        const groupedDates = {};
        !!calendarEvents?.length && calendarEvents.forEach((item) => {
            const dateStart = changeTimeFormat(item?.datetime_start, i18n.language)
            const dateStartDay = dateStart.split(',')?.[0]

            if (!groupedDates[dateStartDay]) {
                groupedDates[dateStartDay] = [item];
            } else {
                groupedDates[dateStartDay].push(item);
            }
        });
        setEventList(groupedDates)
    }, [calendarEvents])

    useEffect(() => {
        if (googleToken) {
            fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                headers: {Authorization: `Bearer ${googleToken}`}
            }).then(response => response.json())
                .then(data => {
                    setGoogleUser(data);
                })
        }
    }, [googleToken])

    const updateEventListCurrentDate = async () => {
        const start = new Date(date?.startDate || null);
        const end = new Date(date?.endDate || null);
        await props.GetCalendarSynchronize()
        await getEventsList(start, end)
    }

    useEffect(() => {
        if (date) {
            const start = new Date(date?.startDate || null);
            const end = new Date(date?.endDate || null);
            props.GetCalendarSynchronize()
            getEventsList(start, end)
        }
    }, [date])

    function getEventsList(dateStart, dateEnd) {
        dateStart.setHours(0, 0, 0, 0);
        dateEnd.setHours(23, 59, 59, 0);

        props.GetCalendarsEvents({
            datetime_start: dateStart.toISOString(),
            datetime_end: dateEnd.toISOString()
        })
    }

    function calendarEventsList() {
        let tomorrow = new Date();
        const dateStart = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setDate({
            startDate: dateStart,
            endDate: tomorrow
        })
        getEventsList(dateStart, tomorrow)
    }

    function calendarSynchronize() {
        setLoading(true)
        props.GetCalendarSynchronize().finally(() => {
            setLoading(false)
        })
    }

    async function handleOnSuccess(response) {
        const isCheckScope = (response?.scope || '').includes("https://www.googleapis.com/auth/calendar.readonly")
        if (isCheckScope) {
            setGoogleUser(response?.hd)
            const res = await props.GoogleAuth({
                code: response?.code,
                scope: response?.scope
            })
            setGoogleSignature(res?.signature)
            await props.GetGoogleAuth();
            setShowSettingsModal(false);
            setLoading(true)
            props.GetCalendarSynchronize()
                .then(()=> calendarEventsList())
                .finally(() => {
                setLoading(false);
            })
            setShowSuccessModal(true);
        } else {
            setShowSettingsModal(false);
            setLoading(true)
            props.GetCalendarSynchronize()
                .then(()=> calendarEventsList())
                .finally(() => {
                    setLoading(false);
                })
            setShowAuthModal(true)
        }
    }

    function closeSuccessModal() {
        setShowSuccessModal(false)
        setLoading(true)
        props.GetCalendarSynchronize()
            .finally(() => {
                setLoading(false);
            })
        setShowSettingsModal(true)
        setGoogleSignature('')
    }

    function closeSettingsModal() {
        setShowSettingsModal(false);
        props.GetCalendarSynchronize()
            .then(()=> calendarEventsList())
            .finally(() => {
                setLoading(false);
            })

        const visibleWelcomeModal = localStorage.getItem(KEY_LOCAL_STORAGE_DONT_VISIBLE_WELCOME_MODAL);
        const visibleShowTwoStep = localStorage.getItem(KEY_LOCAL_STORAGE_FLAG_STEP_ONE);
        if (!visibleWelcomeModal) {
            setShowWelcomeModal(true);
        }

        if(visibleShowTwoStep){
            handleClickNextStep2List();
        }
    }

    function SuccessModal() {

        const getDescription = () => {
            if (i18n.language === 'ru') {
                return `Ваше имя для участия в видеоконференции "${googleSignature}" было добавлено в ваш профиль.
                        Мы будем использовать его для фильтрации информации о вашем участии в видеороликах.`
            } else {
                return `Your name in video conference "${googleSignature}" has been added to your profile.
                        We will use it to filter information about your participation in videos.`
            }
        }
        return (
            <Modal
                classNames={{
                    wrapper: 'success_modal__wrapper',
                }}
                open={showSuccessModal}
                onCancel={closeSuccessModal}
                centered
            >
                <div className="success_modal__container">

                    <img src={label} className="success_modal__container--img"/>
                    <div className="success_modal__container--title">
                        {t('calendar_connected_title')}
                    </div>
                    {googleSignature ?
                        <div className="success_modal__container--description">
                            {getDescription()}
                        </div>
                        : null}
                    <Button
                        variant="primary"
                        size="l"
                        text={t('calendar_connected_btn')}
                        onClick={closeSuccessModal}
                    />
                </div>
            </Modal>
        );
    }

    function changeTime(dates) {
        const [start, end] = dates;
        setDate({
            startDate: start,
            endDate: end
        })

        const dateStart = new Date(start);
        const dateEnd = new Date(end);

        getEventsList(dateStart, dateEnd)
    }

    function getEventsAfterSynchronized() {
        const {startDate, endDate} = date;

        changeTime([startDate, endDate])
    }

    function getRelativeDay(inputDate) {
        const date = dayjs(inputDate);
        const today = dayjs();
        const tomorrow = today.add(1, 'day');
        const yesterday = today.subtract(1, 'day');

        if (date.isSame(today, 'day')) {
            return 'Today';
        } else if (date.isSame(tomorrow, 'day')) {
            return 'Tomorrow';
        } else if (date.isSame(yesterday, 'day')) {
            return 'Yesterday';
        } else {
            return null;
        }
    }

    return (
        <>
            <div className={`calendar ${[STEPS.ONE, STEPS.TWO].includes(activeStep) ? 'calendar-tour' : ''}`}>
                <div className="calendar__header">
                    <div className="left_part">
                        {t('calendar_title')}
                        {(googleToken || outlookInfo?.token?.access_token) && <Synchronize loading={loading}
                                                                                           getEvents={getEventsAfterSynchronized}/>}
                    </div>
                    <div className="right_part">
                        {/*<ContactsIcon/>*/}
                        <SettingsIcon
                            style={{color: '#1ABB78'}}
                            onClick={() => setShowSettingsModal(true)}/>
                    </div>
                </div>
                <div className="calendar__content"
                     ref={targetRef}>
                    {
                        (googleToken || outlookInfo?.token?.access_token) ? <>
                                <DatePicker
                                    selected={date?.startDate}
                                    onChange={(date) => changeTime(date)}
                                    startDate={date?.startDate}
                                    endDate={date?.endDate}
                                    dateFormat="yyyy/MM/dd"
                                    locale="en-GB"
                                    selectsRange
                                    inline
                                />
                                {
                                    hasPermission(ROLE_ITEMS.SELF_PROFILE_CALENDAR_EDIT) &&
                                    Object.entries(eventList).map(([key, list]) => {

                                        return <div className="calendar__day-item" key={key}>
                                            <div className="day-header">
                                                <span className="day-header__title">{getRelativeDay(key) || ''}</span>
                                                <span
                                                    className="day-header__date">{key}</span>
                                            </div>
                                            {
                                                !!list?.length && list?.map(item => {
                                                    return <EventItem key={item?.id} data={item}
                                                                      updateEventsList={updateEventListCurrentDate}/>
                                                })
                                            }
                                        </div>
                                    })
                                }

                            </>
                            :
                            <>
                                {!googleToken && !outlookInfo?.token?.access_token ?
                                    <div className="calendar__auth">
                                        <img src={calendar_img} alt=""/>
                                        <div className="calendar__auth__title">{t('calendar_connect')}</div>
                                        <div className="calendar__auth__description">
                                            {t('calendar_description')}
                                        </div>
                                        <div className="calendar__auth">
                                            <div className="calendar__auth--icons">
                                                <GoogleCalendarIcon/>
                                                <OutlookCalendarIcon/>
                                            </div>
                                            <Button
                                                text={t('connect_calendars')}
                                                variant="secondary"
                                                onClick={() => {
                                                    setShowSettingsModal(true);
                                                    // dispatch(UpdateActiveStep(null));
                                                }}
                                            />

                                        </div>
                                    </div>
                                    : null
                                }
                            </>
                    }
                </div>
                {showSuccessModal && SuccessModal()}
                <SettingsModal showSettingsModal={showSettingsModal}
                               targetRef={targetRef}
                               googleToken={googleToken}
                               clientId={clientId}
                               user={googleUser}
                               setGoogleUser={setGoogleUser}
                               handleOnSuccess={handleOnSuccess}
                               onHide={closeSettingsModal}/>
                <ConnectedCalendarModal onSuccess={() => setShowSettingsModal(true)
                }/>
                {showAuthModal ?
                    <Modal
                        classNames={{
                            wrapper: 'success_modal__wrapper',
                        }}
                        open={showAuthModal}
                        onCancel={() => setShowAuthModal(false)}
                        centered
                    >
                        <GoogleOAuthProvider clientId={userCalendars?.clientId}>

                            <div className="success_modal__container">

                                <img src={labelReauth} className="success_modal__container--img"/>
                                <div className="success_modal__container--title">
                                    {t('reauth_calendar_access')}
                                </div>
                                <div className="success_modal__container--description">
                                    {t('reauth_calendar_desc')}
                                </div>
                                {userCalendars?.clientId ?
                                    <GoogleButton
                                        t={t}
                                        handleOnSuccess={handleOnSuccess}
                                        onClick={() => {
                                            setShowAuthModal(false);
                                        }}
                                    />
                                    : null}
                            </div>
                        </GoogleOAuthProvider>
                    </Modal>
                    : null}
                <WelcomeModal open={showWelcomeModal} onClose={() => setShowWelcomeModal(false)}/>
            </div>
            <TourFirstStep openSettingsModal={()=>setShowSettingsModal(true)}/>
        </>
    )
}

function GoogleButton({handleOnSuccess, t, onClick}) {
    const login = useGoogleLogin({
        flow: 'auth-code',
        scope: 'https://www.googleapis.com/auth/calendar.readonly',
        onSuccess: async (codeResponse) => {
            handleOnSuccess(codeResponse)
        },
        onError: errorResponse => console.log(errorResponse),
    });
    return (
        <Button
            variant="primary"
            size="l"
            text={t('reauth')}
            onClick={() => {
                onClick();
                login();
            }}
        />
    );
}


const mapStateToProps = (state) => {
    return getPropsFromState(state, [
        'calendarEvents', 'clientId', 'googleToken', 'userRoles'
    ])
};

const mapDispatchToProps = {
    GetCalendarsEvents,
    GetCalendarSynchronize,
    GetGoogleAuth,
    GoogleAuth,
};

export default connect(mapStateToProps, mapDispatchToProps)(Calendar);
